import { z } from 'zod';
import {
  BpmSchema,
  FocusLevelSchema,
  GENERATOR_DURATION_MAX,
  GENERATOR_DURATION_MIN,
  GenreSchema,
  HexColorSchema,
  InstrumentSchema,
  MAX_ANIMATION_SECONDS,
  KeySchema,
  MAX_CLIP_LOOPS,
  MAX_FADE_SECONDS,
  MAX_SONG_BARS,
  MoodSchema,
  MotionSchema,
  SectionKindSchema,
  ShapeSchema,
  AV_MAX_TICK,
  EventCategorySchema,
  ReactionTypeSchema,
} from '../domain/schemas';
import type { GenerationKind } from '../domain/schemas';
import { TemplateIdSchema } from '../animation/procedural/spec';

/** Who a choreography step acts on; resolved against the explicit UI context when executed. */
const TargetRefSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('selected') }),
  z.object({ kind: z.literal('selected-character') }),
  /** The selected character, else the only character in the scene. */
  z.object({ kind: z.literal('character') }),
  /** The previous step's targets (or the selection for the first step). */
  z.object({ kind: z.literal('inherit') }),
  /** An object of the active scene by name (ambiguous names are refused). */
  z.object({ kind: z.literal('name'), name: z.string().min(1).max(60) }),
]);

/** Who a sync reaction acts on: an object (resolved in the session's scene) or the whole scene. */
const SyncTargetSchema = z.union([TargetRefSchema, z.object({ kind: z.literal('scene') }), z.object({ kind: z.literal('selected-name'), name: z.string().min(1).max(60) })]);

/**
 * A partially specified composition request parsed from text. Missing fields are
 * filled from the genre's defaults (and a random seed, which is reported back) before
 * the full GeneratorParams are validated.
 */
export const GenerateSpecSchema = z.object({
  genre: GenreSchema,
  bpm: BpmSchema.optional(),
  key: KeySchema.optional(),
  mood: MoodSchema.optional(),
  intensity: z.number().min(0).max(1).optional(),
  complexity: z.number().min(0).max(1).optional(),
  durationSec: z.number().int().min(GENERATOR_DURATION_MIN).max(GENERATOR_DURATION_MAX).optional(),
  seed: z.number().int().min(0).max(2 ** 31 - 1).optional(),
  focus: z.partialRecord(InstrumentSchema, FocusLevelSchema),
});
export type GenerateSpec = z.infer<typeof GenerateSpecSchema>;

/** Every command the deterministic prompt parser can emit. Validated with Zod. */
export const CommandSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('music.play'), mode: z.enum(['pattern', 'song']).optional() }),
  z.object({ type: z.literal('music.stop') }),
  z.object({ type: z.literal('music.setBpm'), bpm: BpmSchema }),
  z.object({ type: z.literal('music.nudgeBpm'), delta: z.number().int().min(-100).max(100) }),
  z.object({ type: z.literal('music.setSwing'), swing: z.number().min(0).max(0.5) }),
  z.object({ type: z.literal('music.mute'), instrument: InstrumentSchema, muted: z.boolean() }),
  z.object({ type: z.literal('music.clear'), instrument: InstrumentSchema }),
  z.object({ type: z.literal('music.resetPattern') }),
  z.object({ type: z.literal('music.randomize'), seed: z.number().int().min(0).max(2 ** 31 - 1).optional() }),
  z.object({ type: z.literal('music.generate'), spec: GenerateSpecSchema }),
  z.object({ type: z.literal('music.adjustIntensity'), group: z.enum(['drums', 'bass', 'melody', 'all']), direction: z.union([z.literal(1), z.literal(-1)]) }),
  z.object({ type: z.literal('music.adjustVolume'), target: z.union([InstrumentSchema, z.literal('master')]), deltaDb: z.number().min(-24).max(24) }),
  z.object({ type: z.literal('music.setVolume'), target: z.union([InstrumentSchema, z.literal('master')]), db: z.number().min(-60).max(6) }),
  z.object({ type: z.literal('music.adjustEffect'), instrument: InstrumentSchema, effect: z.enum(['drive', 'reverbSend', 'delaySend']), delta: z.number().min(-1).max(1) }),
  z.object({ type: z.literal('music.solo'), instrument: InstrumentSchema, solo: z.boolean() }),
  z.object({ type: z.literal('music.pan'), instrument: InstrumentSchema, pan: z.number().min(-1).max(1) }),
  z.object({ type: z.literal('music.setKey'), key: KeySchema }),
  z.object({ type: z.literal('music.setMode'), mode: z.enum(['pattern', 'song']) }),
  z.object({ type: z.literal('music.loopSong'), loop: z.boolean() }),
  z.object({ type: z.literal('arrangement.insert'), kind: SectionKindSchema, relation: z.enum(['before', 'after']), anchor: SectionKindSchema }),
  z.object({ type: z.literal('arrangement.append'), kind: SectionKindSchema, at: z.enum(['start', 'end']) }),
  z.object({ type: z.literal('arrangement.remove'), kind: SectionKindSchema }),
  z.object({ type: z.literal('arrangement.duplicate'), kind: SectionKindSchema }),
  z.object({ type: z.literal('arrangement.resize'), kind: SectionKindSchema, deltaBars: z.number().int().min(-32).max(32) }),
  z.object({ type: z.literal('export.wav') }),
  z.object({ type: z.literal('animation.start') }),
  z.object({ type: z.literal('animation.pause') }),
  z.object({ type: z.literal('animation.reset') }),
  z.object({ type: z.literal('animation.restart') }),
  z.object({ type: z.literal('animation.setBackground'), color: HexColorSchema }),
  z.object({ type: z.literal('animation.setSpeed'), speed: z.number().min(0.1).max(5) }),
  z.object({
    type: z.literal('animation.addShape'),
    shape: ShapeSchema,
    color: HexColorSchema,
    motion: MotionSchema,
  }),
  z.object({ type: z.literal('animation.clearShapes') }),
  // ---- Phase 2: samples and clips. "Selected" means the explicit UI selection.
  z.object({ type: z.literal('sample.assign'), instrument: InstrumentSchema }),
  z.object({ type: z.literal('sample.unassign'), instrument: InstrumentSchema }),
  z.object({
    type: z.literal('sample.place'),
    at: z.discriminatedUnion('kind', [
      z.object({ kind: z.literal('song-start') }),
      z.object({ kind: z.literal('section'), section: SectionKindSchema }),
      z.object({ kind: z.literal('bar'), bar: z.number().int().min(1).max(MAX_SONG_BARS) }),
    ]),
  }),
  z.object({ type: z.literal('clip.reverse'), reverse: z.boolean() }),
  z.object({ type: z.literal('clip.fade'), edge: z.enum(['in', 'out']), seconds: z.number().min(0).max(MAX_FADE_SECONDS) }),
  z.object({ type: z.literal('clip.adjustGain'), deltaDb: z.number().min(-48).max(48) }),
  z.object({ type: z.literal('clip.setGain'), db: z.number().min(-48).max(12) }),
  z.object({ type: z.literal('clip.duplicate') }),
  z.object({ type: z.literal('clip.mute'), muted: z.boolean() }),
  z.object({ type: z.literal('clip.delete') }),
  z.object({ type: z.literal('clip.setRate'), rate: z.number().min(0.25).max(4) }),
  z.object({ type: z.literal('clip.normalize'), normalize: z.boolean() }),
  z.object({ type: z.literal('clip.loop'), loops: z.number().int().min(1).max(MAX_CLIP_LOOPS) }),
  // ---- Phase 3A: scene editor. "Selected" means the explicit UI selection.
  z.object({ type: z.literal('scene.create'), preset: z.enum(['neon-city', 'dark-studio', 'forest', 'blank']) }),
  z.object({
    type: z.literal('scene.addObject'),
    shape: z.enum(['rect', 'circle', 'ellipse', 'triangle', 'text']),
    color: HexColorSchema.optional(),
    text: z.string().min(1).max(200).optional(),
  }),
  z.object({ type: z.literal('object.move'), dx: z.number().min(-10000).max(10000), dy: z.number().min(-10000).max(10000) }),
  z.object({ type: z.literal('object.scale'), factor: z.number().min(0.05).max(20) }),
  z.object({ type: z.literal('object.rotate'), degrees: z.number().min(-360).max(360) }),
  z.object({ type: z.literal('object.order'), where: z.enum(['front', 'back', 'forward', 'backward']) }),
  z.object({ type: z.literal('object.color'), color: HexColorSchema }),
  z.object({ type: z.literal('object.visibility'), visible: z.boolean() }),
  z.object({ type: z.literal('object.lock'), locked: z.boolean() }),
  z.object({ type: z.literal('object.duplicate') }),
  z.object({ type: z.literal('object.delete') }),
  z.object({ type: z.literal('character.create'), preset: z.enum(['modern', 'fantasy', 'futuristic']) }),
  z.object({ type: z.literal('character.place') }),
  z.object({ type: z.literal('layer.visibility'), visible: z.boolean() }),
  z.object({ type: z.literal('layer.lock'), locked: z.boolean() }),
  // ---- Phase 3B: keyframe animation. Uses the current animation, selection and playhead.
  z.object({ type: z.literal('anim.create'), seconds: z.number().positive().max(MAX_ANIMATION_SECONDS), fps: z.union([z.literal(12), z.literal(24), z.literal(30), z.literal(60)]).optional() }),
  z.object({ type: z.literal('anim.fps'), fps: z.union([z.literal(12), z.literal(24), z.literal(30), z.literal(60)]) }),
  z.object({ type: z.literal('anim.duration'), seconds: z.number().positive().max(MAX_ANIMATION_SECONDS) }),
  z.object({ type: z.literal('anim.loop'), mode: z.enum(['once', 'loop', 'pingpong']) }),
  z.object({ type: z.literal('anim.key'), what: z.enum(['position', 'rotation', 'scale', 'opacity', 'transform']), frame: z.number().int().min(0).max(MAX_ANIMATION_SECONDS * 60) }),
  z.object({ type: z.literal('anim.move'), direction: z.enum(['left', 'right', 'up', 'down']), distance: z.number().positive().max(20000).optional(), seconds: z.number().positive().max(MAX_ANIMATION_SECONDS).optional() }),
  z.object({ type: z.literal('anim.rotate'), degrees: z.number().min(-36000).max(36000), seconds: z.number().positive().max(MAX_ANIMATION_SECONDS) }),
  z.object({ type: z.literal('anim.preset'), preset: z.enum(['idle', 'walk', 'run', 'jump', 'float', 'spin']) }),
  // ---- Phase 3C: choreography. Steps of one prompt form one motion plan.
  z.object({
    type: z.literal('motion.step'),
    template: TemplateIdSchema,
    target: TargetRefSchema,
    timing: z
      .object({
        mode: z.enum(['sequence', 'with', 'at']),
        at: z.number().min(0).max(MAX_ANIMATION_SECONDS).nullable(),
        atFrame: z.number().int().min(0).max(MAX_ANIMATION_SECONDS * 60).nullable(),
        delay: z.number().min(0).max(MAX_ANIMATION_SECONDS),
      })
      .optional(),
    duration: z.number().positive().max(MAX_ANIMATION_SECONDS).optional(),
    durationFrames: z.number().int().positive().max(MAX_ANIMATION_SECONDS * 60).optional(),
    repeat: z.number().int().min(1).max(64).optional(),
    params: z.record(z.string().max(32), z.union([z.number(), z.string().max(40), z.boolean()])),
    /** "replacing existing keys": use the replace-range policy instead of merge. */
    replace: z.boolean().optional(),
    /** "create a six-second animation with …": the plan's total length. */
    planSeconds: z.number().positive().max(MAX_ANIMATION_SECONDS).optional(),
    /** "repeat the bounce …" / "start the floating motion …" amend an earlier step of the prompt. */
    amend: z.enum(['repeat', 'start']).optional(),
  }),
  z.object({ type: z.literal('motion.apply') }),
  // ---- Phase 4A: audio-visual sync. Session commands act on the selected sync session.
  z.object({ type: z.literal('sync.create') }),
  z.object({
    type: z.literal('sync.react'),
    reaction: ReactionTypeSchema,
    categories: z.array(EventCategorySchema).min(1).max(4),
    /** Every Nth event (1 = all). */
    every: z.number().int().min(1).max(256),
    target: SyncTargetSchema,
    direction: z.enum(['up', 'down', 'left', 'right']).optional(),
    color: HexColorSchema.optional(),
  }),
  z.object({
    type: z.literal('sync.cue'),
    template: TemplateIdSchema,
    target: SyncTargetSchema,
    tick: z.number().int().min(0).max(AV_MAX_TICK),
    /** Repeat every this many ticks until the music ends (null = once). */
    everyTicks: z.number().int().min(1).max(AV_MAX_TICK).nullable(),
  }),
  /** Scales a reaction's strength; `which` names it by kind ("pulse", "bounce", …) or "selected". */
  z.object({ type: z.literal('sync.strength'), which: z.string().min(1).max(30), factor: z.number().min(0.05).max(20) }),
  z.object({ type: z.literal('sync.remove'), which: z.string().min(1).max(30), category: EventCategorySchema.nullable() }),
  z.object({ type: z.literal('sync.loop'), fromBar: z.number().int().min(1).max(MAX_SONG_BARS), toBar: z.number().int().min(1).max(MAX_SONG_BARS) }),
  z.object({ type: z.literal('sync.loopOff') }),
  /** Moves a motion cue (by motion name) or a manual marker (by label). */
  z.object({ type: z.literal('sync.move'), name: z.string().min(1).max(40), tick: z.number().int().min(0).max(AV_MAX_TICK) }),
  z.object({ type: z.literal('sync.marker'), tick: z.number().int().min(0).max(AV_MAX_TICK), label: z.string().max(40) }),
  z.object({ type: z.literal('sync.play') }),
  z.object({ type: z.literal('sync.pause') }),
]);
export type Command = z.infer<typeof CommandSchema>;

export type ParseErrorKind = 'empty' | 'too-long' | 'too-many' | 'invalid-value' | 'unsupported' | 'generation-unavailable';

export interface ParseError {
  kind: ParseErrorKind;
  message: string;
  segment?: string;
  generationKind?: GenerationKind;
  suggestions: string[];
}

export type ParseResult = { ok: true; commands: Command[] } | { ok: false; error: ParseError };

/** Documented examples, shown in the UI and used for "did you mean" suggestions. */
export const COMMAND_EXAMPLES: { example: string; description: string; area: 'compose' | 'music' | 'mix' | 'arrangement' | 'audio' | 'scene' | 'animation' | 'motion' | 'sync' }[] = [
  {
    example: 'generate a dark brazilian phonk beat at 150 bpm in f minor with heavy 808 bass and aggressive cowbells',
    description: 'Procedural composition (not AI). Genres: brazilian phonk, drift phonk, dark phonk, trap, lo-fi',
    area: 'compose',
  },
  { example: 'create a 60-second drift phonk arrangement', description: 'Duration 8 s – 5 min; add "seed 42" to reproduce', area: 'compose' },
  { example: 'make a chill lofi beat at 80 bpm in g dorian seed 7', description: 'Moods: dark, aggressive, chill, melancholic, energetic', area: 'compose' },
  { example: 'make the drums more intense', description: 'Or less intense; also bass, melody, everything', area: 'music' },
  { example: 'play song', description: 'Play the arrangement ("play pattern" loops the current pattern)', area: 'music' },
  { example: 'stop', description: 'Stop playback and rewind', area: 'music' },
  { example: 'set bpm to 128', description: 'Tempo 60–200; "faster by 10" / "slower"', area: 'music' },
  { example: 'swing 20%', description: 'Swing amount 0–50%', area: 'music' },
  { example: 'set key to f# minor', description: 'Minor, major, phrygian, dorian, harmonic minor', area: 'music' },
  { example: 'clear snare', description: 'Remove every hit from an instrument in the current pattern', area: 'music' },
  { example: 'randomize drums seed 42', description: 'Re-roll the current pattern\'s drums (seed optional)', area: 'music' },
  { example: 'reduce the cowbell volume', description: 'Or raise/boost; "by 6 db"; "set kick volume to -3 db"', area: 'mix' },
  { example: 'mute hats', description: 'Mute / unmute / solo / unsolo any instrument', area: 'mix' },
  { example: 'pan the lead left', description: 'Left, right or center', area: 'mix' },
  { example: 'more distortion on the 808', description: 'Also less; reverb and delay too', area: 'mix' },
  { example: 'add a buildup before the drop', description: 'Sections: intro, verse, buildup, drop, breakdown, outro', area: 'arrangement' },
  { example: 'add an outro at the end', description: 'Or at the start', area: 'arrangement' },
  { example: 'make the drop longer', description: 'Longer/shorter by 4 bars; remove or duplicate a section', area: 'arrangement' },
  { example: 'export wav', description: 'Render the arrangement offline and download a WAV file', area: 'arrangement' },
  { example: 'add the selected sample to the kick track', description: 'Plays the sample selected in the Audio library on a drum track (kick, snare, clap, hats, perc)', area: 'audio' },
  { example: 'place the selected sample at the start of the drop', description: 'Adds it as a clip; also "at the start of the song" or "at bar 9"', area: 'audio' },
  { example: 'reverse the selected clip', description: 'Clip commands act on the clip selected in the Audio timeline', area: 'audio' },
  { example: 'fade in the selected clip over 2 seconds', description: 'Fade in / fade out; seconds or ms', area: 'audio' },
  { example: 'lower the selected clip volume by 6 db', description: 'Or raise; "set the selected clip volume to -3 db"', area: 'audio' },
  { example: 'duplicate the selected clip', description: 'Also mute / unmute / delete / normalize the selected clip', area: 'audio' },
  { example: 'set the selected clip speed to 1.5x', description: 'Speed and pitch change together (no time-stretching)', area: 'audio' },
  { example: 'loop the selected clip 4 times', description: 'Up to 16 repeats', area: 'audio' },
  { example: 'create a neon city scene', description: 'Scene presets: neon city, dark studio, forest landscape, blank', area: 'scene' },
  { example: 'add a red circle', description: 'Shapes: rectangle, square, circle, ellipse, triangle; or “add text Hello”', area: 'scene' },
  { example: 'move the selected object 50 pixels right', description: 'Left, right, up or down; acts on the canvas selection', area: 'scene' },
  { example: 'make the selected object larger', description: 'Larger, smaller, twice as big, half the size, or “scale … by 150%”', area: 'scene' },
  { example: 'move the selected object to the front', description: 'Front, back, forward, backward (within its layer)', area: 'scene' },
  { example: 'make the background dark purple', description: 'Colour names (with dark/light) or #rrggbb', area: 'scene' },
  { example: 'create a futuristic character', description: 'Character presets: modern, fantasy, futuristic', area: 'scene' },
  { example: 'place the selected character in the scene', description: 'Places the character selected in the character studio', area: 'scene' },
  { example: 'hide the selected layer', description: 'Hide/show/lock/unlock the selected layer; also the selected object', area: 'scene' },
  { example: 'create a 4-second animation', description: 'New keyframe animation for the current scene (add "at 30 fps"; 12, 24, 30 or 60)', area: 'animation' },
  { example: 'animate the selected character moving right', description: 'Position keys from the first to the last frame; left/right/up/down, "300 pixels", "over 2 seconds"', area: 'animation' },
  { example: 'add a position keyframe at frame 24', description: 'Keys the selected object; also rotation, scale, opacity or transform keyframes', area: 'animation' },
  { example: 'rotate the selected object 90 degrees over 2 seconds', description: 'Rotation keys from the playhead', area: 'animation' },
  { example: 'make the selected character walk', description: 'Presets: walk, run, idle (characters); jump, float, spin (any object)', area: 'animation' },
  { example: 'loop the current animation', description: 'Or "play the animation once", "ping-pong the animation"', area: 'animation' },
  { example: 'make the selected character run right for 3 seconds', description: 'Procedural motion (deterministic, not AI): run, walk, jump, land, crouch, wave, turn, celebrate, fall, idle', area: 'motion' },
  { example: 'make the character run, then jump, then wave', description: 'Several steps form a plan shown in the Motion Composer; say “apply the motion plan” to add it', area: 'motion' },
  { example: 'jump at frame 48 and land at frame 72', description: 'Start times: “at frame N”, “at 2 seconds”; durations: “for 3 seconds”', area: 'motion' },
  { example: 'make the neon sign pulse three times', description: 'Objects by name (ambiguous names are refused): float, bounce, spin, shake, pulse, sway', area: 'motion' },
  { example: 'fade the title in over one second', description: 'Fade in / fade out; a hidden object becomes visible when it fades in', area: 'motion' },
  { example: 'move the selected object from x 100 to x 500', description: 'Absolute slide (also “to y …”); relative: “make it slide left 200 pixels”', area: 'motion' },
  { example: 'start the floating motion at 2 seconds', description: 'Amends the step in the same prompt, or adds one; “repeat the bounce four times”', area: 'motion' },
  { example: 'make the character follow this path', description: 'Follows the path drawn in the Motion Composer', area: 'motion' },
  { example: 'create a six-second animation with running and jumping', description: 'New animation plus a plan that fills it', area: 'motion' },
  { example: 'apply the motion plan', description: 'Adds the Motion Composer’s plan as editable keyframes (one undo step)', area: 'motion' },
  { example: 'synchronize this animation with the phonk track', description: 'Creates a sync session: the music, the active scene and its animation on one clock (Sync tab)', area: 'sync' },
  { example: 'pulse the neon sign on every kick', description: 'Reactions: pulse, bounce, spin, fade, glow, hide/show; events: kick, snare, clap, hats, perc, cowbell, 808, beat, bar, marker', area: 'sync' },
  { example: 'make the character bounce on every snare', description: 'Also "on every second kick"; acts on the session’s scene', area: 'sync' },
  { example: 'flash the background on the first beat of each bar', description: 'Scene effects: flash the background / the screen, shake the camera', area: 'sync' },
  { example: 'make the character jump every four bars', description: 'Motion cue (Phase 3C motion) on the musical timeline; "start the running animation at bar 3"', area: 'sync' },
  { example: 'increase the pulse strength', description: 'Or decrease / "by 50%"; acts on the selected reaction or the only one of that kind', area: 'sync' },
  { example: 'remove the kick reaction', description: 'Removes the reaction to that event (asks when several match)', area: 'sync' },
  { example: 'loop bars 1 through 8', description: 'Loops sound and picture together; "stop looping"', area: 'sync' },
  { example: 'move the jump to beat 16', description: 'Moves a motion cue or a manual marker ("add a marker at bar 5 called drop")', area: 'sync' },
  { example: 'preview the synchronized animation', description: 'Plays the session (music + animation + reactions); "pause the sync preview"', area: 'sync' },
  { example: 'set animation fps to 24', description: 'Keyframes keep their timing; "set the animation duration to 6 seconds"', area: 'animation' },
  { example: 'start animation', description: 'Play / pause / restart / reset the animation', area: 'animation' },
  { example: 'set background to navy', description: 'Colour name or #rrggbb', area: 'animation' },
  { example: 'add a red circle that orbits', description: 'Shapes: circle, square, triangle; motions: orbit, pulse, bounce, spin', area: 'animation' },
];
